import React, { useState } from "react";
import { 
  CheckCircle2, 
  ExternalLink, 
  Copy, 
  Mail,
  Building2,
  Users,
  UserPlus,
  Shield,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  HelpCircle,
  Play
} from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const RESPONSIA_EMAIL = "api@responsia.fr"; // Email à configurer

const GoogleSetupGuide = () => {
  const [expandedStep, setExpandedStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState([]);

  const copyEmail = () => {
    navigator.clipboard.writeText(RESPONSIA_EMAIL);
    toast.success("Email copié !");
  };

  const toggleStep = (step) => {
    setExpandedStep(expandedStep === step ? null : step);
  };

  const markCompleted = (step) => {
    if (!completedSteps.includes(step)) {
      setCompletedSteps([...completedSteps, step]);
    }
    if (step < 5) {
      setExpandedStep(step + 1);
    }
  };

  const steps = [
    {
      number: 1,
      title: "Connectez-vous à Google Business Profile",
      icon: Building2,
      content: (
        <div className="space-y-4">
          <p className="text-slate-600">
            Accédez à votre fiche d'établissement Google avec le compte propriétaire ou administrateur.
          </p>
          <a 
            href="https://business.google.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
            Ouvrir Google Business Profile
          </a>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mt-4">
            <p className="text-sm text-amber-800">
              <strong>💡 Astuce :</strong> Utilisez le compte Google qui a créé ou qui gère votre fiche d'établissement.
            </p>
          </div>
        </div>
      )
    },
    {
      number: 2,
      title: "Sélectionnez votre établissement",
      icon: Building2,
      content: (
        <div className="space-y-4">
          <p className="text-slate-600">
            Si vous avez plusieurs établissements, cliquez sur celui que vous souhaitez connecter à ResponsIA.
          </p>
          <div className="bg-slate-100 rounded-xl p-4">
            <div className="flex items-center gap-3 p-3 bg-white rounded-lg border-2 border-violet-500">
              <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                <Building2 className="w-5 h-5 text-violet-600" />
              </div>
              <div>
                <p className="font-medium text-slate-900">Votre Restaurant</p>
                <p className="text-sm text-slate-500">123 Rue de Paris</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      number: 3,
      title: "Accédez aux paramètres d'accès",
      icon: Users,
      content: (
        <div className="space-y-4">
          <p className="text-slate-600">
            Dans le menu de votre fiche, trouvez les paramètres de gestion des utilisateurs :
          </p>
          <ol className="space-y-3">
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 bg-violet-100 text-violet-700 rounded-full flex items-center justify-center text-sm font-medium shrink-0">1</span>
              <span className="text-slate-700">Cliquez sur les <strong>3 points</strong> (menu) ou <strong>Paramètres</strong></span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 bg-violet-100 text-violet-700 rounded-full flex items-center justify-center text-sm font-medium shrink-0">2</span>
              <span className="text-slate-700">Sélectionnez <strong>"Business Profile settings"</strong></span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 bg-violet-100 text-violet-700 rounded-full flex items-center justify-center text-sm font-medium shrink-0">3</span>
              <span className="text-slate-700">Cliquez sur <strong>"People and access"</strong> (Personnes et accès)</span>
            </li>
          </ol>
          <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
            <p className="text-sm text-slate-600 mb-2">📍 Chemin :</p>
            <div className="flex items-center gap-2 text-sm flex-wrap">
              <span className="bg-white px-2 py-1 rounded border">Menu</span>
              <ArrowRight className="w-4 h-4 text-slate-400" />
              <span className="bg-white px-2 py-1 rounded border">Business Profile settings</span>
              <ArrowRight className="w-4 h-4 text-slate-400" />
              <span className="bg-violet-100 text-violet-700 px-2 py-1 rounded border border-violet-300 font-medium">People and access</span>
            </div>
          </div>
        </div>
      )
    },
    {
      number: 4,
      title: "Ajoutez ResponsIA comme Manager",
      icon: UserPlus,
      content: (
        <div className="space-y-4">
          <p className="text-slate-600">
            Invitez ResponsIA à gérer votre fiche en ajoutant notre adresse email :
          </p>
          
          {/* Email to copy */}
          <div className="bg-violet-50 border-2 border-violet-200 rounded-xl p-4">
            <p className="text-sm text-violet-700 mb-2 font-medium">📧 Email à ajouter :</p>
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-white rounded-lg px-4 py-3 border border-violet-200">
                <span className="font-mono text-lg text-slate-900">{RESPONSIA_EMAIL}</span>
              </div>
              <Button onClick={copyEmail} className="bg-violet-600 hover:bg-violet-700 rounded-lg">
                <Copy className="w-4 h-4 mr-2" />
                Copier
              </Button>
            </div>
          </div>

          <ol className="space-y-3">
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 bg-violet-100 text-violet-700 rounded-full flex items-center justify-center text-sm font-medium shrink-0">1</span>
              <span className="text-slate-700">Cliquez sur <strong>"Add"</strong> ou <strong>"Invite"</strong></span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 bg-violet-100 text-violet-700 rounded-full flex items-center justify-center text-sm font-medium shrink-0">2</span>
              <span className="text-slate-700">Collez l'email : <strong>{RESPONSIA_EMAIL}</strong></span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 bg-violet-100 text-violet-700 rounded-full flex items-center justify-center text-sm font-medium shrink-0">3</span>
              <div className="text-slate-700">
                <span>Choisissez le rôle : <strong>"Manager"</strong></span>
                <p className="text-sm text-slate-500 mt-1">
                  (Manager permet de gérer les avis sans donner les droits de propriétaire)
                </p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 bg-violet-100 text-violet-700 rounded-full flex items-center justify-center text-sm font-medium shrink-0">4</span>
              <span className="text-slate-700">Cliquez sur <strong>"Invite"</strong> ou <strong>"Send"</strong></span>
            </li>
          </ol>

          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-emerald-800">Sécurité</p>
                <p className="text-sm text-emerald-700">
                  Le rôle "Manager" permet uniquement de répondre aux avis et voir les statistiques. 
                  Vous restez propriétaire de votre fiche.
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      number: 5,
      title: "C'est terminé !",
      icon: CheckCircle2,
      content: (
        <div className="space-y-4">
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <h3 className="text-xl font-semibold text-emerald-800 mb-2">
              Connexion en cours !
            </h3>
            <p className="text-emerald-700">
              Notre équipe va accepter l'invitation sous 24h. 
              Vous recevrez une notification une fois la connexion établie.
            </p>
          </div>

          <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
            <p className="font-medium text-slate-900 mb-3">✨ Ce que ResponsIA pourra faire :</p>
            <ul className="space-y-2">
              <li className="flex items-center gap-2 text-slate-700">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                Voir vos avis Google en temps réel
              </li>
              <li className="flex items-center gap-2 text-slate-700">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                Générer des réponses personnalisées par IA
              </li>
              <li className="flex items-center gap-2 text-slate-700">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                Publier les réponses automatiquement (si activé)
              </li>
              <li className="flex items-center gap-2 text-slate-700">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                Synchroniser vos statistiques
              </li>
            </ul>
          </div>
        </div>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-violet-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-violet-100 text-violet-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Play className="w-4 h-4" />
            Guide de configuration
          </div>
          <h1 className="text-3xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900 mb-3">
            Connecter votre Google Business Profile
          </h1>
          <p className="text-slate-600">
            Suivez ces 5 étapes simples pour permettre à ResponsIA de gérer vos avis Google.
          </p>
        </div>

        {/* Time estimate */}
        <div className="flex items-center justify-center gap-6 mb-8">
          <div className="flex items-center gap-2 text-slate-600">
            <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center">
              ⏱️
            </div>
            <span className="text-sm">~2 minutes</span>
          </div>
          <div className="flex items-center gap-2 text-slate-600">
            <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center">
              📱
            </div>
            <span className="text-sm">Ordinateur recommandé</span>
          </div>
        </div>

        {/* Steps */}
        <div className="space-y-4">
          {steps.map((step) => {
            const Icon = step.icon;
            const isExpanded = expandedStep === step.number;
            const isCompleted = completedSteps.includes(step.number);

            return (
              <div 
                key={step.number}
                className={`bg-white rounded-2xl border-2 transition-all ${
                  isCompleted 
                    ? "border-emerald-200 bg-emerald-50/30" 
                    : isExpanded 
                      ? "border-violet-300 shadow-lg shadow-violet-100" 
                      : "border-slate-200"
                }`}
              >
                {/* Step Header */}
                <button
                  onClick={() => toggleStep(step.number)}
                  className="w-full p-4 flex items-center gap-4 text-left"
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${
                    isCompleted 
                      ? "bg-emerald-100" 
                      : isExpanded 
                        ? "bg-violet-100" 
                        : "bg-slate-100"
                  }`}>
                    {isCompleted ? (
                      <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                    ) : (
                      <span className={`text-xl font-bold ${
                        isExpanded ? "text-violet-600" : "text-slate-400"
                      }`}>
                        {step.number}
                      </span>
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className={`font-semibold ${
                      isCompleted ? "text-emerald-800" : "text-slate-900"
                    }`}>
                      {step.title}
                    </h3>
                  </div>
                  {isExpanded ? (
                    <ChevronUp className="w-5 h-5 text-slate-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  )}
                </button>

                {/* Step Content */}
                {isExpanded && (
                  <div className="px-4 pb-4">
                    <div className="pl-16">
                      {step.content}
                      
                      {step.number < 5 && (
                        <Button
                          onClick={() => markCompleted(step.number)}
                          className="mt-6 bg-violet-600 hover:bg-violet-700 rounded-xl"
                        >
                          {isCompleted ? "✓ Terminé" : "J'ai terminé cette étape"}
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Help section */}
        <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center shrink-0">
              <HelpCircle className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900 mb-1">Besoin d'aide ?</h3>
              <p className="text-slate-600 text-sm mb-3">
                Si vous rencontrez des difficultés, notre équipe est là pour vous aider.
              </p>
              <a 
                href="mailto:support@responsia.com" 
                className="inline-flex items-center gap-2 text-violet-600 hover:text-violet-700 font-medium text-sm"
              >
                <Mail className="w-4 h-4" />
                support@responsia.com
              </a>
            </div>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-slate-400 mt-8">
          © 2026 ResponsIA - Gestion intelligente des avis Google
        </p>
      </div>
    </div>
  );
};

export default GoogleSetupGuide;
